import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ReactPlaceholder from 'react-placeholder';
import {TextBlock} from 'react-placeholder/lib/placeholders';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import InfiniteScroll from 'react-infinite-scroller';
import Moment from 'react-moment';
import MomentCore from 'moment';

import {
	makeStyles,
	useTheme,
	useMediaQuery,
	Fab,
	Paper,
	Grid,
	Typography,
	Card,
	CardContent,
	CardActionArea
} from '@material-ui/core';

import Fido from "../utils/Fido";
import Constants from "../utils/Constants";
import {usePrevious} from "../utils/CustomHooks";
import {isJsonEqual} from "../utils/Json";
import PaddedLayout from "../components/PaddedLayout";
import SearchForm from "../components/SearchForm";
import FilterSelect from "../components/FilterSelect";
import FullscreenMessage from '../components/FullscreenMessage';
import CircularLoadingIndicator from "../components/CircularLoadingIndicator";

const styles = makeStyles(theme => ({
	eventGridContainer: {
		// The following style is taken from the class MuiTGrid-container
		// Also adding spacing={2} which comes from MuiGrid-spacing
		display: "flex",
		flexWrap: "wrap",
		boxSizing: "border-box",
		width: "100%",
		marginBottom: theme.spacing(4)
	},

	eventGridItem: {
		// The following styles are taken from the class MuiGrid-spacing > MuiGrid-item
		padding: theme.spacing(1)
	},

	title: {
		fontSize: "1.5em",
		fontWeight: "bold",
		marginTop: 0,
		lineHeight: "1.75em",
		paddingBottom: theme.spacing(.5)
	},

	map: {
		maxWidth: "100%",
		height: "auto",
		display: "block"
	},

	card: {
		maxWidth: theme.spacing(76.25),
		margin: "0 auto"
	},

	cardAction: {
		maxWidth: theme.spacing(76.25)
	},

	cardContent: {
		paddingTop: theme.spacing(2),
		paddingBottom: theme.spacing(2),
		paddingLeft: theme.spacing(2),
		paddingRight: theme.spacing(2)
	},

	cardDates: {
		fontSize: "1rem",
		paddingBottom: theme.spacing(.5)
	},

	statusCell: {
		[theme.breakpoints.down('xs')]: {
			paddingLeft: 6
		}
	},

	statusIcon: {
		width: "0.55rem !important",
		paddingRight: theme.spacing(1)
	},

	statusActive: {
		color: theme.palette.primary.main
	},

	statusInactive: {
		color: theme.palette.text.secondary
	},

	fab: {
		zIndex: 1,
		color: theme.palette.common.white,

		[theme.breakpoints.down('xs')]: {
			left: theme.spacing(2.5)
		}
	},

	fabExtendedIcon: {
		marginRight: theme.spacing(1)
	},

	fabLink: {
		textDecoration: "none"
	},

	paddedLayout: {
		paddingBottom: theme.spacing(3)
	},

	searchForm: {
		paddingRight: theme.spacing(2)
	},

	statusFilter: {
		marginTop: theme.spacing(2.5),
		marginRight: theme.spacing(2)
	},

	monthHeading: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(1)
	},

	fullScreenClassName: {
		[theme.breakpoints.only('md')]: {
			paddingTop: `${theme.spacing(27.5)}px !important`
		},

		[theme.breakpoints.only('sm')]: {
			paddingTop: `${theme.spacing(26.375)}px !important`
		}
	}
}));

const placeholderColor = Constants.COLORS.PLACEHOLDER_BG;
const itemPlaceHolder = (
	<div>
		<Paper style={{padding: 10, marginTop: 10, maxWidth: 610, margin: "0 auto"}}>
			<div style={{display: "flex", marginTop: 25, flexDirection: "column"}}>
				<div style={{flex: 1}}>
					<TextBlock rows={2} color={placeholderColor} style={{width: "95%"}} />
				</div>
				<div style={{flex: 1, marginTop: 10}}>
					<TextBlock rows={2} color={placeholderColor} style={{width: "100%"}} />
				</div>
			</div>
		</Paper>
	</div>
);

const allStatuses = Constants.EVENT.STATUSES;
const defaultStatuses = [{
	id: "active",
	name: "Active"
}, {
	id: "future",
	name: "Future"
}];

function ProtectedEntities(props) {
	const [selectedStatuses, setSelectedStatuses] = useState(
		defaultStatuses.map(status => status.id)
	);
	const [entities, setEntities] = useState(null);
	const [paging, setPaging] = useState({
		lastPage: 1,
		hasMore: false
	});
	const [search, setSearch] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [groupedData, setGroupedData] = useState({});

	const theme = useTheme();
	const atLeastSmall = useMediaQuery(theme.breakpoints.up("sm"));

	let classes = styles(),
		fullScreenClassName = classes.fullScreenClassName,
		{updateHeader, resetHeader, type, showSnackbar, location, history, setFullScreenClassName} = props,
		name = Constants.ENTITIES.getName(type),
		pluralName = Constants.ENTITIES.getName(type, true),
		isEvents = type === Constants.ENTITIES.IDS.EVENT;

	// update headerbar, setFullScreenClassName
	useEffect(() => {
		setFullScreenClassName(fullScreenClassName);
		updateHeader({title: pluralName, primary: true});
		return () => resetHeader();
	}, [updateHeader, resetHeader, pluralName, type, setFullScreenClassName, fullScreenClassName]);

	// group by month
	let prevEntities = usePrevious(entities);
	useEffect(() => {
		if (entities != null && !isJsonEqual(entities, prevEntities)) {
			let grouper = (entity) => {
				if (isEvents) {
					// activity not desconstructed yet, so using published_at iso time; also note create moment
					// as UTC (from server) so can show in local date
					let moment = new MomentCore.utc(entity.start_date);
					return moment.format('MMMM YYYY');
				} else {
					return "group";
				}
			};

			entities.map(item => {
				let group = grouper(item);

				if(!(group in groupedData)) {
					groupedData[group] = [];
				}

				if (groupedData[group].findIndex(entity => entity.id === item.id) === -1) {
					groupedData[group].push(item);
				}

				return item;
			});
		}
	}, [entities, groupedData, setGroupedData, isEvents, prevEntities]);

	// first data load / filtering / searching
	useEffect(() => {
		let laika = new Fido();
		setIsLoading(true);
		setEntities(null);
		setGroupedData({});
		(async function loadItems() {
			let data = await laika.fetch(`/entities${search ? "/search" : ""}`, {
				query: {
					type: type,
					search: search,
					status: isEvents ? selectedStatuses : null
				}
			})
				.catch(error => {
					return showSnackbar(error.message);
				});

			if (data) {
				// TODO: * filter out event (by id) that was just removed but might still be in list that comes back (async)
				setIsLoading(false);
				setEntities(data.entities);
				setPaging({
					lastPage: data.lastPage || 1,
					hasMore: data.hasMoreItems
				});
			}
		})();

		return () => laika.dropIt();
	}, [setEntities, setPaging, selectedStatuses, showSnackbar, type, search, isEvents]);

	const loadPage = (page) => {
		if (paging.hasMore && !isLoading) {
			let fidget = new Fido();
			setIsLoading(true);

			(async function loadNextPage() {
				let data = await fidget.fetch("/entities", {
					query: {
						page: page,
						type: type,
						search: search,
						status: selectedStatuses
					}
				})
					.catch(error => {
						return showSnackbar(error.message);
					});

				if (data) {
					// append new items
					setIsLoading(false);
					setEntities([...entities, ...data.entities]);
					setPaging({
						lastPage: data.lastPage,
						hasMore: data.hasMoreItems
					});
				}
			})();
		}
	};

	const filterStatus = (selected) => {
		if (!isJsonEqual(selectedStatuses, selected.status)) {
			setSelectedStatuses(selected.status);
		}
	};

	const searchHandler = (formVals) => {
		setSearch(formVals.search);
	};

	const clearHandler = () => {
		setSearch(null);
	};

	let cardSizing = {xs:12, sm:4, md:4, lg:4, xl:3},
		pathPrefix = `/protect/${location.pathname.split('/')[2]}`;

	return (
		<PaddedLayout
			noTopPad
			isLarge
			className={classes.paddedLayout}>
			<Grid
				container
				alignItems="center"
				justify="space-between">
				<Grid item xs={3}>
					<Link to={`${pathPrefix}/add`} tabIndex={-1} className={classes.fabLink}>
						{atLeastSmall ? (
							<Fab
								variant="extended"
								color="primary"
								aria-label={`Add ${name}`}
								className={classes.fab}>
								<FontAwesomeIcon
									title="add"
									icon={["far", "plus"]}
									className={classes.fabExtendedIcon} /> Add {name}
							</Fab>
						) : (
							<Fab
								color="primary"
								aria-label={`Add ${name}`}
								className={classes.fab}>
								<FontAwesomeIcon title="add" icon={["far", "plus"]} />
							</Fab>
						)}
					</Link>
				</Grid>
				<Grid item xs={9}>
					<Grid container justify="flex-end">
						<Grid item xs={1} sm={1} md={1} lg={1} xl={1}>&nbsp;</Grid>
						<Grid item xs={8}>
							<SearchForm
								formClass={classes.searchForm}
								searchHandler={searchHandler}
								clearHandler={clearHandler} />
						</Grid>
						{isEvents ?
							<Grid item>
								<FilterSelect
									name="status"
									includeSelectAll
									menuTitle="Status"
									selectAllTitle="All Statuses"
									options={allStatuses}
									defaultSelections={defaultStatuses.map(item => item.id).join(",")}
									className={classes.statusFilter}
									handleUpdate={filterStatus} />
							</Grid> : null
						}
					</Grid>
				</Grid>
			</Grid>
			{entities ?
				entities.length ?
					<React.Fragment>
						<InfiniteScroll
							initialLoad={false}
							pageStart={1}
							loader={null}
							loadMore={loadPage}
							hasMore={paging.hasMore && !isLoading}>
							{Object.keys(groupedData).map((key, idx) => {
								return (
									<React.Fragment key={idx}>
										{isEvents ?
											<Typography variant="h6" className={classes.monthHeading}>{key}</Typography> : null
										}
										<Grid
											container
											className={classes.eventGridContainer}>
											{groupedData[key].map(n => {
												let oneDay = (n.start_date === n.end_date) ? true : false,
													firstLocation = (n.locations.length > 0) ? n.locations[0] : null,
													twoLocations = (firstLocation && n.locations.length === 2) ? 1 : null,
													moreLocations = (firstLocation && n.locations.length > 2) ? n.locations.length - 1 : null;

												return (
													<Grid
														key={n.id}
														item {...cardSizing}
														role="group"
														aria-disabled="false"
														className={classes.eventGridItem}>
														<Card className={classes.card}>
															<CardActionArea className={classes.cardAction} onClick={() => history.push(`${pathPrefix}/${n.id}`)}>
																<CardContent className={classes.cardContent}>
																	<Typography variant="h2" noWrap className={classes.title}>
																		{n.name}
																	</Typography>
																	{isEvents ?
																		<Typography className={classes.cardDates} variant="body1">
																			<Moment format={"MMM Do"}>{n.start_date}</Moment>
																			{!oneDay ? (
																				<span>
																					&nbsp;-&nbsp;
																					<Moment format={"MMM Do"}>{n.end_date}</Moment>
																				</span>
																			) : null}
																		</Typography> : null
																	}
																	<Typography noWrap color="textSecondary" variant="caption" component="p">
																		{firstLocation}
																	</Typography>
																	{!twoLocations && !moreLocations ? (
																		<Typography noWrap color="textSecondary" variant="caption">&nbsp;</Typography>
																	) : null}
																	{twoLocations ? (
																		<Typography noWrap color="textSecondary" variant="caption">{twoLocations} additonal location
																		</Typography>
																	) : null}
																	{moreLocations ? (
																		<Typography noWrap color="textSecondary" variant="caption">{moreLocations} additional locations
																		</Typography>
																	) : null}
																</CardContent>
															</CardActionArea>
														</Card>
													</Grid>
												);
											})}
										</Grid>
									</React.Fragment>
								);
							})}
						</InfiniteScroll>
						{isLoading ?
							<CircularLoadingIndicator /> : null
						}
					</React.Fragment>
					:
					<FullscreenMessage message="No Results" />
				:
				<div className={classes.eventGridContainer}>
					<Grid className={classes.eventGridItem} item {...cardSizing}>
						<ReactPlaceholder
							ready={false}
							customPlaceholder={itemPlaceHolder}>
							<span></span>
						</ReactPlaceholder>
					</Grid>
					<Grid className={classes.eventGridItem} item {...cardSizing}>
						<ReactPlaceholder
							ready={false}
							customPlaceholder={itemPlaceHolder}>
							<span></span>
						</ReactPlaceholder>
					</Grid>
					<Grid className={classes.eventGridItem} item {...cardSizing}>
						<ReactPlaceholder
							ready={false}
							customPlaceholder={itemPlaceHolder}>
							<span></span>
						</ReactPlaceholder>
					</Grid>
				</div>
			}
		</PaddedLayout>
	);
}

ProtectedEntities.propTypes = {
	history: PropTypes.object.isRequired,
	location: PropTypes.object.isRequired,
	updateHeader: PropTypes.func.isRequired,
	resetHeader: PropTypes.func.isRequired,
	showSnackbar: PropTypes.func.isRequired,
	type: PropTypes.number,
	banner: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
	bannerHeight: PropTypes.number,
	hasNudgeBanner: PropTypes.bool,
	setFullScreenClassName: PropTypes.func
};

export default ProtectedEntities;